import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface CalibrationRecord {
  id: string
  device_id: string
  device_name: string
  device_type: string
  calibration_type: 'standard' | 'precision' | 'quality_control' | 'linearity' | 'accuracy'
  status: 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'cancelled'
  priority: 'low' | 'normal' | 'high' | 'critical'
  scheduled_date: string
  started_date?: string
  completed_date?: string
  technician: string
  standard_reference: string
  target_values: CalibrationTarget[]
  measured_values: CalibrationMeasurement[]
  tolerance_limits: ToleranceLimit[]
  results: CalibrationResult
  certification_number?: string
  next_calibration_date?: string
  notes: string
  attachments: string[]
  created_at: string
  updated_at: string
}

interface CalibrationTarget {
  parameter: string
  target_value: number
  unit: string
  tolerance: number
}

interface CalibrationMeasurement {
  parameter: string
  measured_value: number
  unit: string
  deviation: number
  within_tolerance: boolean
}

interface ToleranceLimit {
  parameter: string
  lower_limit: number
  upper_limit: number
  unit: string
}

interface CalibrationResult {
  overall_status: 'pass' | 'fail' | 'conditional_pass'
  accuracy_percentage: number
  passed_parameters: number
  total_parameters: number
  calibration_factor?: number
  uncertainty: number
  environmental_conditions: {
    temperature: number
    humidity: number
    pressure: number
  }
}

interface CalibrationSchedule {
  id: string
  device_id: string
  device_name: string
  calibration_type: string
  frequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual' | 'bi_annual'
  last_calibration: string
  next_due: string
  is_overdue: boolean
  days_overdue: number
  assigned_technician: string
  reminder_sent: boolean
}

const CalibrationManagement: React.FC = () => {
  const [records, setRecords] = useState<CalibrationRecord[]>([])
  const [schedules, setSchedules] = useState<CalibrationSchedule[]>([])
  const [selectedRecord, setSelectedRecord] = useState<CalibrationRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'records' | 'schedule' | 'overdue'>('records')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [,] = useState(false)

  const [formData, setFormData] = useState({
    device_id: '',
    calibration_type: 'standard' as const,
    priority: 'normal' as const,
    scheduled_date: new Date().toISOString().split('T')[0],
    technician: '',
    standard_reference: '',
    notes: ''
  })

  const fetchRecords = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`/api/v1/devices/calibration/records/?search=${searchTerm}&status=${statusFilter}&type=${typeFilter}`)
      if (response.ok) {
        const data = await response.json()
        setRecords(data.results || data)
      } else {
        toast.error('Failed to fetch calibration records')
      }
    } catch (error) {
      console.error('Error fetching records:', error)
      toast.error('Error fetching records')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSchedules = async () => {
    try {
      const response = await fetch('/api/v1/devices/calibration/schedules/')
      if (response.ok) {
        const data = await response.json()
        setSchedules(data.results || data)
      }
    } catch (error) {
      console.error('Error fetching schedules:', error)
    }
  }

  useEffect(() => {
    fetchRecords()
    fetchSchedules()
  }, [searchTerm, statusFilter, typeFilter])

  const createCalibrationRecord = async () => {
    try {
      const response = await fetch('/api/v1/devices/calibration/records/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Calibration scheduled successfully')
        setShowCreateModal(false)
        fetchRecords()
        fetchSchedules()
      } else {
        toast.error('Failed to schedule calibration')
      }
    } catch (error) {
      console.error('Error creating record:', error)
      toast.error('Error creating record')
    }
  }

  const startCalibration = async (recordId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/calibration/records/${recordId}/start/`, {
        method: 'POST'
      })

      if (response.ok) {
        toast.success('Calibration started')
        fetchRecords()
      } else {
        toast.error('Failed to start calibration')
      }
    } catch (error) {
      console.error('Error starting calibration:', error)
      toast.error('Error starting calibration')
    }
  }


  const generateCertificate = async (recordId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/calibration/records/${recordId}/certificate/`, {
        method: 'GET'
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `calibration_certificate_${recordId}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        toast.success('Certificate downloaded')
      } else {
        toast.error('Failed to generate certificate')
      }
    } catch (error) {
      console.error('Error generating certificate:', error)
      toast.error('Error generating certificate')
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getResultColor = (status: string) => {
    const colors: Record<string, string> = {
      pass: 'bg-green-100 text-green-800',
      fail: 'bg-red-100 text-red-800',
      conditional_pass: 'bg-yellow-100 text-yellow-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      standard: 'bg-blue-100 text-blue-800',
      precision: 'bg-purple-100 text-purple-800',
      quality_control: 'bg-green-100 text-green-800',
      linearity: 'bg-orange-100 text-orange-800',
      accuracy: 'bg-red-100 text-red-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const overdueSchedules = schedules.filter(s => s.is_overdue)
  const upcomingSchedules = schedules.filter(s => !s.is_overdue && new Date(s.next_due) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calibration Management</h1>
          <p className="text-gray-600">Manage device calibrations, schedules, and certificates</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Schedule Calibration
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
            Bulk Schedule
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {[
          { label: 'Total Calibrations', value: records.length, color: 'blue', icon: '⚙️' },
          { label: 'Completed', value: records.filter(r => r.status === 'completed').length, color: 'green', icon: '✅' },
          { label: 'In Progress', value: records.filter(r => r.status === 'in_progress').length, color: 'yellow', icon: '🔄' },
          { label: 'Overdue', value: overdueSchedules.length, color: 'red', icon: '🚨' },
          { label: 'Pass Rate', value: `${Math.round((records.filter(r => r.results?.overall_status === 'pass').length / records.filter(r => r.status === 'completed').length) * 100) || 0}%`, color: 'purple', icon: '📊', isText: true }
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className={`text-2xl font-bold text-gray-900 ${stat.isText ? 'text-xl' : ''}`}>
                  {stat.value}
                </p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {(overdueSchedules.length > 0 || upcomingSchedules.length > 0) && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Calibration Alerts</h3>
          <div className="space-y-3">
            {overdueSchedules.map((schedule) => (
              <div key={schedule.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-red-900">{schedule.device_name}</p>
                    <p className="text-sm text-red-700">
                      {schedule.calibration_type} calibration overdue by {schedule.days_overdue} days
                    </p>
                  </div>
                  <button className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700">
                    Schedule Now
                  </button>
                </div>
              </div>
            ))}
            {upcomingSchedules.map((schedule) => (
              <div key={schedule.id} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-yellow-900">{schedule.device_name}</p>
                    <p className="text-sm text-yellow-700">
                      {schedule.calibration_type} calibration due: {new Date(schedule.next_due).toLocaleDateString()}
                    </p>
                  </div>
                  <button className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700">
                    Schedule
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'records', label: `Calibration Records (${records.length})` },
            { key: 'schedule', label: `Schedule (${schedules.length})` },
            { key: 'overdue', label: `Overdue (${overdueSchedules.length})` }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search calibrations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="standard">Standard</option>
              <option value="precision">Precision</option>
              <option value="quality_control">Quality Control</option>
              <option value="linearity">Linearity</option>
              <option value="accuracy">Accuracy</option>
            </select>
          </div>
        </div>
      </div>

      {/* Records Tab */}
      {activeTab === 'records' && (
        <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device / Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Technician
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Results
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Loading calibration records...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No calibration records found.
                    </td>
                  </tr>
                ) : (
                  records.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{record.device_name}</div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(record.calibration_type)}`}>
                            {record.calibration_type.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                          {record.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.technician}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.results ? (
                          <div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getResultColor(record.results.overall_status)}`}>
                              {record.results.overall_status.replace('_', ' ').toUpperCase()}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {record.results.accuracy_percentage}% accuracy
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Pending</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <div>Scheduled: {new Date(record.scheduled_date).toLocaleDateString()}</div>
                          {record.completed_date && (
                            <div>Completed: {new Date(record.completed_date).toLocaleDateString()}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedRecord(record)
                              setShowDetailModal(true)
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View
                          </button>
                          {record.status === 'scheduled' && (
                            <button
                              onClick={() => startCalibration(record.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Start
                            </button>
                          )}
                          {record.status === 'completed' && record.certification_number && (
                            <button
                              onClick={() => generateCertificate(record.id)}
                              className="text-purple-600 hover:text-purple-900"
                            >
                              Certificate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-4">
          {schedules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No calibration schedules found</p>
            </div>
          ) : (
            schedules.map((schedule) => (
              <div key={schedule.id} className="bg-white rounded-lg border p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{schedule.device_name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{schedule.calibration_type.replace('_', ' ')}</p>
                    <p className="text-xs text-gray-500">Frequency: {schedule.frequency.replace('_', ' ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      Next Due: {new Date(schedule.next_due).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      Technician: {schedule.assigned_technician}
                    </p>
                    {schedule.is_overdue && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        {schedule.days_overdue} days overdue
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Calibration Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Schedule Calibration</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Device ID</label>
                <input
                  type="text"
                  value={formData.device_id}
                  onChange={(e) => setFormData({...formData, device_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Calibration Type</label>
                <select
                  value={formData.calibration_type}
                  onChange={(e) => setFormData({...formData, calibration_type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="standard">Standard</option>
                  <option value="precision">Precision</option>
                  <option value="quality_control">Quality Control</option>
                  <option value="linearity">Linearity</option>
                  <option value="accuracy">Accuracy</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Date</label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Technician</label>
                <input
                  type="text"
                  value={formData.technician}
                  onChange={(e) => setFormData({...formData, technician: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Standard Reference</label>
                <input
                  type="text"
                  value={formData.standard_reference}
                  onChange={(e) => setFormData({...formData, standard_reference: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={createCalibrationRecord}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Schedule Calibration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Calibration Details - {selectedRecord.device_name}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Device:</span> {selectedRecord.device_name}</p>
                    <p><span className="font-medium">Type:</span> {selectedRecord.calibration_type}</p>
                    <p><span className="font-medium">Technician:</span> {selectedRecord.technician}</p>
                    <p><span className="font-medium">Standard:</span> {selectedRecord.standard_reference}</p>
                    <p><span className="font-medium">Cert. Number:</span> {selectedRecord.certification_number || 'N/A'}</p>
                  </div>
                </div>

                {selectedRecord.results && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Results</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Status:</span>
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getResultColor(selectedRecord.results.overall_status)}`}>
                          {selectedRecord.results.overall_status.replace('_', ' ').toUpperCase()}
                        </span>
                      </p>
                      <p><span className="font-medium">Accuracy:</span> {selectedRecord.results.accuracy_percentage}%</p>
                      <p><span className="font-medium">Parameters:</span> {selectedRecord.results.passed_parameters}/{selectedRecord.results.total_parameters} passed</p>
                      <p><span className="font-medium">Uncertainty:</span> ±{selectedRecord.results.uncertainty}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Scheduled:</span> {new Date(selectedRecord.scheduled_date).toLocaleDateString()}</p>
                    {selectedRecord.started_date && (
                      <p><span className="font-medium">Started:</span> {new Date(selectedRecord.started_date).toLocaleDateString()}</p>
                    )}
                    {selectedRecord.completed_date && (
                      <p><span className="font-medium">Completed:</span> {new Date(selectedRecord.completed_date).toLocaleDateString()}</p>
                    )}
                    {selectedRecord.next_calibration_date && (
                      <p><span className="font-medium">Next Due:</span> {new Date(selectedRecord.next_calibration_date).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>

                {selectedRecord.results?.environmental_conditions && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Environmental Conditions</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Temperature:</span> {selectedRecord.results.environmental_conditions.temperature}°C</p>
                      <p><span className="font-medium">Humidity:</span> {selectedRecord.results.environmental_conditions.humidity}%</p>
                      <p><span className="font-medium">Pressure:</span> {selectedRecord.results.environmental_conditions.pressure} hPa</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedRecord.measured_values.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-2">Measurement Results</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Measured</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deviation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedRecord.measured_values.map((measurement, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 text-sm text-gray-900">{measurement.parameter}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{measurement.measured_value} {measurement.unit}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{measurement.deviation}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              measurement.within_tolerance ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {measurement.within_tolerance ? 'PASS' : 'FAIL'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedRecord.notes && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {selectedRecord.notes}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
              {selectedRecord.status === 'completed' && selectedRecord.certification_number && (
                <button
                  onClick={() => generateCertificate(selectedRecord.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Download Certificate
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CalibrationManagement